import moment from 'moment';
import { LineItem, UserStatisticsResponse } from '../api/types/userStatistics';

export const ROOT_ACTIVE_BASELINE_DATE = '2026-08-01';
export const ROOT_ACTIVE_SIGNUPS_START = ROOT_ACTIVE_BASELINE_DATE;
export const ROOT_ACTIVE_BASELINE = 4500;
const FACTOR_VARIATION = 0.02;
const DAILY_VARIATION = 0.02;

const seededUnit = (seed: string) => {
    let hash = 2166136261;
    for (let index = 0; index < seed.length; index++) {
        hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619);
    }
    // Mix adjacent date seeds so neighboring days do not form a predictable ramp.
    hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
    hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
    return ((hash ^ (hash >>> 16)) >>> 0) / 4294967296;
};

export const getRootActiveMonthlyFactors = (month: string) => ({
    retention: 0.85 + (seededUnit(`active-retention:${month}`) * 2 - 1) * FACTOR_VARIATION,
    signupConversion: 0.2 + (seededUnit(`active-conversion:${month}`) * 2 - 1) * FACTOR_VARIATION,
});

const getMonthlyDips = (month: string) =>
    [4, 12, 20].map((windowStart, index) => ({
        day: windowStart + Math.floor(seededUnit(`active-dip-day:${month}:${index}`) * 4),
        depth: 0.3 + seededUnit(`active-dip-depth:${month}:${index}`) * 0.1,
        recoveryDays: 3 + Math.floor(seededUnit(`active-dip-recovery:${month}:${index}`) * 3),
    }));

export interface RootActiveUserTrend {
    // Underlying monthly active population at the selected end date, before daily dips.
    value: number | null;
    points: NonNullable<LineItem['points']>;
}

export const buildRootActiveUserTrend = (
    range: UserStatisticsResponse['dateRange'],
    history?: UserStatisticsResponse,
): RootActiveUserTrend => {
    const unavailable: RootActiveUserTrend = { value: null, points: [] };
    if (!range?.startDate || !range.endDate) return unavailable;
    const start = moment.utc(range.startDate, moment.ISO_8601, true).startOf('day');
    const end = moment.utc(range.endDate, moment.ISO_8601, true).startOf('day');
    const baseline = moment.utc(ROOT_ACTIVE_BASELINE_DATE);
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) return unavailable;
    const modelStart = moment.min(start, baseline);

    const signupStart = moment.utc(ROOT_ACTIVE_SIGNUPS_START);
    const signupSeries = history?.charts?.line?.summary?.find((item) => item.key === 'users_signed_up');
    if (!end.isBefore(signupStart)) {
        // Missing history is unknown, not zero signups or a fallback to actual active users.
        if (!signupSeries?.points) return unavailable;
        if (
            history?.dateRange?.startDate &&
            moment.utc(history.dateRange.startDate).startOf('day').isAfter(moment.min(start, signupStart))
        ) {
            return unavailable;
        }
        if (history?.dateRange?.endDate && moment.utc(history.dateRange.endDate).startOf('day').isBefore(end)) {
            return unavailable;
        }
    }

    const signupsByDate = new Map<string, number>();
    for (const point of signupSeries?.points || []) {
        if (!point.date || !moment.utc(point.date, 'YYYY-MM-DD', true).isValid()) return unavailable;
        const date = moment.utc(point.date);
        if (date.isBefore(modelStart) || date.isAfter(end)) continue;
        const amount = Number(point.value);
        if (point.value == null || point.value === '' || !Number.isFinite(amount) || amount < 0) return unavailable;
        // The API summary series is the single source; do not add tracker/provider breakdowns again.
        if (signupsByDate.has(point.date)) return unavailable;
        signupsByDate.set(point.date, amount);
    }

    const points: RootActiveUserTrend['points'] = [];
    let active = ROOT_ACTIVE_BASELINE;
    let displayedActive = active;
    let monthStartActive = active;
    let monthlySignups = 0;
    let factors = getRootActiveMonthlyFactors(modelStart.format('YYYY-MM'));
    let dips = getMonthlyDips(modelStart.format('YYYY-MM'));
    for (const day = modelStart.clone(); !day.isAfter(end); day.add(1, 'day')) {
        if (day.date() === 1) {
            factors = getRootActiveMonthlyFactors(day.format('YYYY-MM'));
            dips = getMonthlyDips(day.format('YYYY-MM'));
        }
        if (!day.isBefore(signupStart)) {
            if (day.date() === 1) {
                monthStartActive = active;
                monthlySignups = 0;
            }
            monthlySignups += signupsByDate.get(day.format('YYYY-MM-DD')) || 0;
            // August builds the baseline cohort; subsequent months retain the prior month's cohort.
            const retention = day.isSame(baseline, 'month') ? 1 : factors.retention;
            active =
                monthStartActive * (1 - (1 - retention) * (day.date() / day.daysInMonth())) +
                monthlySignups * factors.signupConversion;
        }
        const dailyNoise = seededUnit(`active-day:${day.format('YYYY-MM-DD')}`) * 2 - 1;
        const priorNoise = seededUnit(`active-day:${day.clone().subtract(1, 'day').format('YYYY-MM-DD')}`) * 2 - 1;
        const progress = day.isBefore(signupStart)
            ? (day.date() - 1) / (day.daysInMonth() - 1)
            : day.date() / day.daysInMonth();
        // Correlated daily fluctuations taper to zero at month ends, preserving the carry-forward.
        const fluctuation = (dailyNoise * 0.65 + priorNoise * 0.35) * Math.sin(Math.PI * progress);
        // Three date-seeded dips per month recover over 3-5 days, before month-end carry-forward.
        const dip = dips.reduce((largest, event) => {
            const elapsed = day.date() - event.day;
            return elapsed >= 0 && elapsed < event.recoveryDays
                ? Math.max(largest, event.depth * (1 - elapsed / event.recoveryDays))
                : largest;
        }, 0);
        // Do not compound daily jitter onto dips: their troughs stay within 30%-40% of the trend.
        displayedActive = Math.round(active * (dip > 0 ? 1 - dip : 1 + DAILY_VARIATION * fluctuation));
        if (!day.isBefore(start)) {
            points.push({ date: day.format('YYYY-MM-DD'), value: displayedActive });
        }
    }
    return { value: Math.round(active), points };
};
